/**
 * Insurance-valid gap justification reasons.
 *
 * Every gap justification must carry one of these coded reasons to be accepted
 * by the insurance company. The `value` (with the code prefix) is what gets
 * stored on the gap justification's `reason` field.
 *
 * `text` supplies the default free-text Vermerk note, one wording per
 * cancelling side. The side is baked into the wording ("Patient krank" vs
 * "Therapeut krank") so the Vermerk alone says who caused the gap — the coded
 * reason cannot express that, since the insurance codes are side-agnostic.
 *
 * The mapping is deliberately one-directional: picking a reason *prefills* the
 * Vermerk, but the two are stored independently and the user stays free to
 * write their own text.
 */

/** Who caused the cancellation. Selects the Vermerk wording. */
export type CancellationSide = "patient" | "therapist";

export interface InsuranceJustificationReason {
  code: "K" | "F" | "T";
  /** Default Vermerk text per side; the user may overwrite it. */
  text: Record<CancellationSide, string>;
  /** Coded value persisted on the gap justification's `reason` field. */
  value: string;
}

export const INSURANCE_JUSTIFICATION_REASONS: InsuranceJustificationReason[] = [
  {
    code: "K",
    text: { patient: "Patient krank", therapist: "Therapeut krank" },
    value: "(K) Krankheit",
  },
  {
    code: "F",
    text: { patient: "Patient im Urlaub", therapist: "Therapeut im Urlaub" },
    value: "(F) Ferien/Urlaub",
  },
  {
    code: "T",
    text: {
      patient: "Patient nach Rücksprache mit dem Arzt",
      therapist: "Therapeut nach Rücksprache mit dem Arzt",
    },
    value: "(T) Nach Rücksprache mit dem Arzt",
  },
];

/**
 * "There is no insurance-valid reason" — a deliberate choice, not an empty
 * field. The Begründung dropdown is mandatory, so this is how a user records a
 * gap the insurance company will not accept and still gets past the dialog.
 *
 * Never sent as `gapJustificationReason`: the backend writes a GapJustification
 * only for a real coded reason, so picking this leaves the gap unjustified —
 * which is exactly what it says. Its value doubles as its label so that, like
 * every real reason, what the user sees is what the option carries.
 */
export const NO_INSURANCE_REASON = "Keine versicherungsgültige Begründung";

export const findInsuranceReasonByValue = (
  value?: string | null,
): InsuranceJustificationReason | undefined =>
  INSURANCE_JUSTIFICATION_REASONS.find((reason) => reason.value === value);

/**
 * The coded reason to send to the backend, or `undefined` when the user picked
 * {@link NO_INSURANCE_REASON} (or picked nothing) — no gap justification.
 */
export const toGapJustificationReason = (
  value?: string | null,
): string | undefined =>
  value && value !== NO_INSURANCE_REASON ? value : undefined;

/** Default Vermerk text for a picked reason on the given side. */
export const vermerkTextFor = (
  value: string | undefined | null,
  side: CancellationSide,
): string | undefined => findInsuranceReasonByValue(value)?.text[side];

/**
 * Free-text Vermerk suggestions per side. Derived from the reason table so the
 * suggestions and the prefilled text can never drift apart.
 */
export const VERMERK_SUGGESTIONS: Record<CancellationSide, string[]> = {
  patient: INSURANCE_JUSTIFICATION_REASONS.map((reason) => reason.text.patient),
  therapist: [
    ...INSURANCE_JUSTIFICATION_REASONS.map((reason) => reason.text.therapist),
    // No code of its own — a therapist's sick child is still "(K) Krankheit"
    // to the insurance company, but the Vermerk should say which it was.
    "Therapeut kindkrank",
  ],
};
